import type { LocalDate, RecurrenceRule } from '@/data/db';
import { toLocalDate } from '@/lib/dates/local-date';

/**
 * 重複規則的下一次日期（spec 9.4、data-model 3.5）。
 *
 * 產生策略是「完成後才產生下一次」，所以這裡只算「從某一天往後的下一次」，
 * 不會一次展開一整年。日期溢位採**夾住**策略：
 * monthDay = 31 遇到 2 月取該月最後一日，不順延到 3 月 1 日。
 */

const WEEKDAY_NAMES = ['日', '一', '二', '三', '四', '五', '六'] as const;

function toDate(value: LocalDate): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/** 把日期夾在該月的合法範圍內。 */
function clampToMonth(year: number, monthIndex: number, day: number): Date {
  return new Date(year, monthIndex, Math.min(day, daysInMonth(year, monthIndex)));
}

export function nextOccurrence(rule: RecurrenceRule, from: LocalDate): LocalDate | null {
  const current = toDate(from);
  let next: Date | null = null;

  switch (rule.frequency) {
    case 'daily': {
      next = new Date(current);
      next.setDate(next.getDate() + 1);
      break;
    }

    case 'weekly': {
      const weekdays = [...(rule.weekdays ?? [])].sort((a, b) => a - b);
      if (weekdays.length === 0) return null;

      // 從隔天開始找最近的指定星期，最多找七天。
      for (let offset = 1; offset <= 7; offset += 1) {
        const candidate = new Date(current);
        candidate.setDate(candidate.getDate() + offset);
        if (weekdays.includes(candidate.getDay())) {
          next = candidate;
          break;
        }
      }
      break;
    }

    case 'monthly': {
      if (rule.monthDay == null) return null;
      // 本月的規則日還沒到就用本月的，不要無條件跳到下個月（和 yearly 同一套判斷）。
      const thisMonth = clampToMonth(current.getFullYear(), current.getMonth(), rule.monthDay);
      next =
        thisMonth > current
          ? thisMonth
          : clampToMonth(current.getFullYear(), current.getMonth() + 1, rule.monthDay);
      break;
    }

    case 'yearly': {
      if (rule.yearMonth == null || rule.yearDay == null) return null;
      const thisYear = clampToMonth(current.getFullYear(), rule.yearMonth - 1, rule.yearDay);
      next =
        thisYear > current
          ? thisYear
          : clampToMonth(current.getFullYear() + 1, rule.yearMonth - 1, rule.yearDay);
      break;
    }
  }

  if (!next) return null;

  const result = toLocalDate(next);
  if (rule.endDate && result > rule.endDate) return null;
  return result;
}

export function describeRecurrence(rule: RecurrenceRule): string {
  let base: string;

  switch (rule.frequency) {
    case 'daily':
      base = '每天';
      break;
    case 'weekly': {
      const names = [...(rule.weekdays ?? [])]
        .sort((a, b) => a - b)
        .map((day) => WEEKDAY_NAMES[day])
        .join('、');
      base = names ? `每週${names}` : '每週';
      break;
    }
    case 'monthly':
      base = rule.monthDay ? `每月 ${rule.monthDay} 日` : '每月';
      break;
    case 'yearly':
      base =
        rule.yearMonth && rule.yearDay ? `每年 ${rule.yearMonth} 月 ${rule.yearDay} 日` : '每年';
      break;
  }

  return rule.endDate ? `${base}，到 ${rule.endDate} 為止` : base;
}

export const RECURRENCE_FREQUENCY_LABELS = {
  daily: '每天',
  weekly: '每週指定星期',
  monthly: '每月指定日期',
  yearly: '每年指定日期',
} as const;
